import { useId, useRef, useState, type ReactNode } from 'react';
import { useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, CalendarClock, FileUp, Pause, Play, Save, Send, Trash2, Undo2, UsersRound, X, XCircle } from 'lucide-react';
import {
  botaoPareceParar,
  CAMPOS_VARIAVEL,
  LIMITES_BROADCAST,
  LIMITES_24H,
  lerPlanilha,
  problemasDoBroadcast,
  resumoTemplate,
  tipoDoTemplate,
  type BotaoBroadcast,
  type ExtrasBroadcast,
  type FontePublico,
  type LinhaPlanilha,
  type OrigemVariavel,
  type ProblemaBroadcast,
  type Publico,
  type ResumoTemplate,
} from '@malgoos/shared';
import { Abas, PainelAba } from '../../componentes/abas';
import { Botao, Cartao, Indicador, Segmentado, Selo } from '../../componentes/base';
import { Estado, Vazio } from '../../componentes/estados';
import { BuscaRemota, CampoData, Entrada, Rotulo, Selecao } from '../../componentes/formulario';
import { Confirmar } from '../../componentes/sobreposicao';
import { Tabela, type Coluna } from '../../componentes/tabela';
import { TextoCortado } from '../../componentes/texto-cortado';
import { useToast } from '../../componentes/toast';
import { get, getTodos, mensagemDeErro, patch, post } from '../../nucleo/api';
import { dataHora, nomePessoa, numero, textoLivre } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import { BASE_BROADCAST, chaveBroadcasts, COR_DESTINATARIO, COR_ESTADO_BROADCAST, telefoneTela, type Broadcast, type Destinatario, type Previa, type TemplateOpcao } from './tipos-broadcast';
import { LinkVoltar } from '../../nucleo/voltar';

type Aba = 'configuracao' | 'relatorio';
const ID_ABAS = 'ficha-broadcast';

/** O que a configuração do rascunho guarda na tela até o "Salvar". */
interface Rascunho {
  template: { nome: string; idioma: string } | null;
  variaveis: OrigemVariavel[];
  extras: ExtrasBroadcast;
  botoes: BotaoBroadcast[];
  publico: Publico;
  ritmo: number;
  limite_24h: number | null;
}

/** JSON com as chaves em ordem (o banco devolve o jsonb com outra ordem: a comparação não pode depender dela). */
function estavel(v: unknown): string {
  if (Array.isArray(v)) return `[${v.map(estavel).join(',')}]`;
  if (v && typeof v === 'object') {
    return `{${Object.keys(v)
      .sort()
      .map((k) => `${JSON.stringify(k)}:${estavel((v as Record<string, unknown>)[k])}`)
      .join(',')}}`;
  }
  return JSON.stringify(v ?? null);
}

const doBroadcast = (b: Broadcast): Rascunho => ({
  template: b.template ? { nome: b.template.nome, idioma: b.template.idioma } : null,
  variaveis: b.variaveis,
  extras: b.extras,
  botoes: b.botoes,
  publico: b.publico,
  ritmo: b.ritmo,
  limite_24h: b.limite_24h,
});

/** Configuração padrão para um template recém-escolhido: variáveis em branco e o botão com cara de "Parar" já ligado a ele. */
function padraoDoTemplate(r: ResumoTemplate): Pick<Rascunho, 'variaveis' | 'extras' | 'botoes'> {
  const fixo = (): OrigemVariavel => ({ origem: 'fixo', valor: '' });
  return {
    variaveis: Array.from({ length: r.variaveis }, (_, i) => (i === 0 ? { origem: 'campo', campo: 'primeiro_nome' } : fixo())),
    extras: {
      cabecalho: r.cabecalho?.tipo === 'texto' ? (r.cabecalho.variaveis ? { variaveis: Array.from({ length: r.cabecalho.variaveis }, fixo) } : null) : r.cabecalho ? { link: '' } : null,
      botoes: r.botoes.flatMap((b): ExtrasBroadcast['botoes'] => (b.tipo === 'link_dinamico' ? [{ indice: b.indice, tipo: 'link' as const, valor: fixo() }] : b.tipo === 'codigo' ? [{ indice: b.indice, tipo: 'codigo' as const, codigo: '' }] : [])),
    },
    botoes: r.botoes.filter((b) => b.tipo === 'resposta').map((b) => ({ indice: b.indice, acao: botaoPareceParar(b.rotulo) ? 'parar' : 'nada', fluxo_id: null })),
  };
}

function publicoVazio(p: Publico, planilhaLinhas: number): boolean {
  return (
    (p.fonte === 'etiquetas' && !p.etiquetas.length) ||
    (p.fonte === 'cadastros' && !p.parceiros.length) ||
    (p.fonte === 'segmento' && !p.tags.length && !p.setores.length) ||
    (p.fonte === 'planilha' && !planilhaLinhas)
  );
}

function Campo({ rotulo, ajuda, children }: { rotulo: string; ajuda?: string; children: (id: string) => ReactNode }) {
  const id = useId();
  return (
    <Rotulo id={id} rotulo={rotulo} ajuda={ajuda}>
      {children(id)}
    </Rotulo>
  );
}

/**
 * U14 F5: ficha do disparo do Broadcast. Configuração (template aprovado, variáveis ligadas a campos do cadastro ou da
 * planilha, botões ligados ao "Parar de receber" ou a uma automação, público com o consentimento, ritmo e limite do
 * número), prévia do público, enviar agora ou agendar, e o relatório por destinatário e por botão.
 */
export function FichaBroadcast() {
  const { id = '' } = useParams();
  const q = useQuery({
    queryKey: [...chaveBroadcasts, 'ficha', id],
    queryFn: () => get<Broadcast>(`/automacoes/broadcasts/${id}`),
    refetchInterval: (c) => (c.state.data?.estado === 'enviando' ? 5_000 : false),
  });
  return <Estado consulta={q}>{(b) => <Ficha key={b.id} b={b} />}</Estado>;
}

function Ficha({ b }: { b: Broadcast }) {
  const { t } = useTranslation('automacoes');
  const [aba, setAba] = useState<Aba>(b.estado === 'rascunho' ? 'configuracao' : 'relatorio');
  const [rascunho, setRascunho] = useState<Rascunho>(() => doBroadcast(b));
  const [planilhaNova, setPlanilhaNova] = useState<{ linhas: LinhaPlanilha[]; colunas: string[] } | null | undefined>(undefined);
  const sujo = planilhaNova !== undefined || estavel(rascunho) !== estavel(doBroadcast(b));
  return (
    <div className="flex min-w-0 flex-col gap-5" data-ficha-broadcast={b.id}>
      <Cabecalho b={b} rascunho={rascunho} planilhaNova={planilhaNova} sujo={sujo} aoSalvar={() => setPlanilhaNova(undefined)} />
      {!b.conversas && <p className="text-sm text-laranja">{t('broadcast.semConversas')}</p>}
      <Abas<Aba> idBase={ID_ABAS} rotulo={b.nome} ativa={aba} aoMudar={setAba} itens={(['configuracao', 'relatorio'] as const).map((k) => ({ chave: k, rotulo: t(`broadcast.abas.${k}`) }))} />
      <PainelAba idBase={ID_ABAS} chave={aba}>
        {aba === 'configuracao' ? <Configuracao b={b} r={rascunho} setR={setRascunho} planilhaNova={planilhaNova} setPlanilhaNova={setPlanilhaNova} sujo={sujo} /> : <Relatorio b={b} />}
      </PainelAba>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Cabeçalho e ações
// ---------------------------------------------------------------------------

function Cabecalho({ b, rascunho, planilhaNova, sujo, aoSalvar }: { b: Broadcast; rascunho: Rascunho; planilhaNova: { linhas: LinhaPlanilha[]; colunas: string[] } | null | undefined; sujo: boolean; aoSalvar: () => void }) {
  const { t } = useTranslation('automacoes');
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const [confirmar, setConfirmar] = useState<'disparar' | 'cancelar' | null>(null);
  const [agendarPara, setAgendarPara] = useState('');
  const podeEditar = s.pode('automacoes.editar');
  // Val5 (29/10): disparar, agendar, pausar, retomar e cancelar o envio em massa pedem `automacoes.disparar`.
  const podeDisparar = s.pode('automacoes.disparar');
  const atualizar = () => qc.invalidateQueries({ queryKey: chaveBroadcasts });

  const salvar = useMutation({
    mutationFn: () =>
      patch(`/automacoes/broadcasts/${b.id}`, {
        ...(b.estado === 'rascunho'
          ? { template: rascunho.template, variaveis: rascunho.variaveis, extras: rascunho.extras, botoes: rascunho.botoes, publico: rascunho.publico, ...(planilhaNova !== undefined ? { planilha: planilhaNova } : {}) }
          : {}),
        ritmo: rascunho.ritmo,
        limite_24h: rascunho.limite_24h,
      }),
    onSuccess: async () => {
      aoSalvar();
      toast.sucesso(t('broadcast.acoes.salvo'));
      await atualizar();
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const disparar = useMutation({
    mutationFn: () => post<{ estado: string; recebem: number }>(`/automacoes/broadcasts/${b.id}/disparar`, { agendado_para: agendarPara ? new Date(agendarPara).toISOString() : null }),
    onSuccess: async (r) => {
      setConfirmar(null);
      toast.sucesso(r.estado === 'agendado' ? t('broadcast.acoes.agendado', { quando: dataHora(new Date(agendarPara).toISOString()), n: numero(r.recebem) }) : t('broadcast.acoes.enviado', { n: numero(r.recebem) }));
      await atualizar();
    },
    onError: (e) => {
      setConfirmar(null);
      toast.erro(mensagemDeErro(e));
    },
  });
  const comando = useMutation({
    mutationFn: (acao: 'pausar' | 'retomar' | 'cancelar' | 'desagendar') => post(`/automacoes/broadcasts/${b.id}/comando`, { acao }),
    onSuccess: async () => {
      setConfirmar(null);
      toast.sucesso(t('broadcast.acoes.feito'));
      await atualizar();
    },
    onError: (e) => {
      setConfirmar(null);
      toast.erro(mensagemDeErro(e));
    },
  });

  return (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        <LinkVoltar para={`${BASE_BROADCAST}/broadcast`} className="inline-flex items-center gap-1 text-sm text-tinta-3 hover:text-tinta-1">
          <ArrowLeft className="size-4" aria-hidden />
          {t('broadcast.voltar')}
        </LinkVoltar>
        <TextoCortado como="h2" className="mt-1 text-xl font-semibold tracking-tight">
          {b.nome}
        </TextoCortado>
        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-tinta-3">
          <Selo cor={COR_ESTADO_BROADCAST[b.estado]}>{t(`broadcast.estados.${b.estado}`)}</Selo>
          {b.tipo && <Selo>{t(`broadcast.tipos.${b.tipo}`)}</Selo>}
          {b.estado === 'agendado' && b.agendado_para && <span>{t('broadcast.envio.agendadoPara', { quando: dataHora(b.agendado_para) })}</span>}
          {b.iniciado_em && <span>{dataHora(b.iniciado_em)}</span>}
        </div>
        {b.motivo && <p className="mt-2 text-sm text-laranja">{t(`broadcast.motivos.${b.motivo}`, { defaultValue: textoLivre(b.motivo) })}</p>}
      </div>
      {(podeEditar || podeDisparar) && (
        <div className="flex flex-wrap items-end gap-2">
          {podeEditar && (b.estado === 'rascunho' || b.estado === 'pausado') && sujo && (
            <Botao icone={<Save className="size-4" />} carregando={salvar.isPending} onClick={() => salvar.mutate()}>
              {t('broadcast.acoes.salvar')}
            </Botao>
          )}
          {podeDisparar && b.estado === 'rascunho' && (
            <>
              <div className="w-56">
                <Campo rotulo={t('broadcast.envio.agendarPara')}>{(id) => <CampoData id={id} type="datetime-local" value={agendarPara} onChange={(e) => setAgendarPara(e.target.value)} />}</Campo>
              </div>
              <Botao
                variante="primario"
                icone={agendarPara ? <CalendarClock className="size-4" /> : <Send className="size-4" />}
                onClick={() => (sujo ? toast.erro(t('broadcast.acoes.salveAntes')) : setConfirmar('disparar'))}
              >
                {agendarPara ? t('broadcast.acoes.agendar') : t('broadcast.acoes.disparar')}
              </Botao>
            </>
          )}
          {podeDisparar && b.estado === 'agendado' && (
            <Botao icone={<Undo2 className="size-4" />} carregando={comando.isPending} onClick={() => comando.mutate('desagendar')}>
              {t('broadcast.acoes.desagendar')}
            </Botao>
          )}
          {podeDisparar && (b.estado === 'enviando' || b.estado === 'agendado') && (
            <Botao icone={<Pause className="size-4" />} carregando={comando.isPending} onClick={() => comando.mutate('pausar')}>
              {t('broadcast.acoes.pausar')}
            </Botao>
          )}
          {podeDisparar && b.estado === 'pausado' && (
            <Botao variante="primario" icone={<Play className="size-4" />} carregando={comando.isPending} onClick={() => comando.mutate('retomar')}>
              {t('broadcast.acoes.retomar')}
            </Botao>
          )}
          {podeDisparar && b.estado !== 'concluido' && b.estado !== 'cancelado' && (
            <Botao variante="perigo" icone={<XCircle className="size-4" />} onClick={() => setConfirmar('cancelar')}>
              {t('broadcast.acoes.cancelar')}
            </Botao>
          )}
        </div>
      )}
      <Confirmar
        aberto={confirmar === 'disparar'}
        aoFechar={() => setConfirmar(null)}
        aoConfirmar={() => disparar.mutate()}
        carregando={disparar.isPending}
        titulo={t('broadcast.confirmar.dispararTitulo')}
        texto={t('broadcast.confirmar.dispararTexto')}
      />
      <Confirmar
        aberto={confirmar === 'cancelar'}
        aoFechar={() => setConfirmar(null)}
        aoConfirmar={() => comando.mutate('cancelar')}
        carregando={comando.isPending}
        perigoso
        titulo={t('broadcast.confirmar.cancelarTitulo')}
        texto={t('broadcast.confirmar.cancelarTexto')}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Configuração
// ---------------------------------------------------------------------------

function Configuracao({
  b,
  r,
  setR,
  planilhaNova,
  setPlanilhaNova,
  sujo,
}: {
  b: Broadcast;
  r: Rascunho;
  setR: (f: (r: Rascunho) => Rascunho) => void;
  planilhaNova: { linhas: LinhaPlanilha[]; colunas: string[] } | null | undefined;
  setPlanilhaNova: (p: { linhas: LinhaPlanilha[]; colunas: string[] } | null | undefined) => void;
  sujo: boolean;
}) {
  const { t } = useTranslation('automacoes');
  const s = useSessao();
  const rascunho = b.estado === 'rascunho' && s.pode('automacoes.editar');
  const ritmoEditavel = (b.estado === 'rascunho' || b.estado === 'pausado') && s.pode('automacoes.editar');
  const templates = useQuery({ queryKey: ['conversas', 'templates', 'aprovados'], queryFn: () => getTodos<TemplateOpcao>('/conversas/templates', { status: 'aprovado' }), enabled: b.conversas, staleTime: 60_000 });
  const escolhido = r.template ? (templates.data?.itens ?? []).find((x) => x.nome === r.template!.nome && x.idioma === r.template!.idioma) : undefined;
  const resumo: ResumoTemplate | null = escolhido ? resumoTemplate(escolhido.componentes) : (b.template?.resumo ?? null);
  const categoria = escolhido?.categoria ?? b.template?.categoria ?? null;
  const tipo = categoria ? tipoDoTemplate(categoria) : null;
  const colunas = planilhaNova !== undefined ? (planilhaNova?.colunas ?? []) : b.planilha_colunas;
  const linhasPlanilha = planilhaNova !== undefined ? (planilhaNova?.linhas.length ?? 0) : b.planilha_linhas;
  const problemas: ProblemaBroadcast[] = (() => {
    if (!resumo) return ['broadcast.template_categoria'];
    const p = problemasDoBroadcast({ tipo, template: resumo, variaveis: r.variaveis, extras: r.extras, botoes: r.botoes, colunas: r.publico.fonte === 'planilha' ? colunas : [] });
    if (publicoVazio(r.publico, linhasPlanilha)) p.push('broadcast.publico_vazio');
    return p;
  })();

  const escolherTemplate = (chave: string) => {
    const tpl = (templates.data?.itens ?? []).find((x) => `${x.nome}|${x.idioma}` === chave);
    if (!tpl) return setR((x) => ({ ...x, template: null }));
    setR((x) => ({ ...x, template: { nome: tpl.nome, idioma: tpl.idioma }, ...padraoDoTemplate(resumoTemplate(tpl.componentes)) }));
  };
  const opcoesTemplate = (templates.data?.itens ?? []).filter((x) => x.categoria !== 'autenticacao').map((x) => ({ valor: `${x.nome}|${x.idioma}`, rotulo: `${x.nome} (${x.idioma}, ${t(`broadcast.template.categorias.${x.categoria}`, { defaultValue: x.categoria })})` }));
  if (r.template && !escolhido) opcoesTemplate.push({ valor: `${r.template.nome}|${r.template.idioma}`, rotulo: `${r.template.nome} (${r.template.idioma})` });

  const origemPara = (o: OrigemVariavel, aoMudar: (o: OrigemVariavel) => void, rotulo: string) => (
    <OrigemCampo key={rotulo} rotulo={rotulo} o={o} colunas={r.publico.fonte === 'planilha' ? colunas : []} aoMudar={aoMudar} editavel={rascunho} />
  );

  return (
    <div className="grid min-w-0 gap-4 lg:grid-cols-2" data-config-broadcast>
      <div className="flex min-w-0 flex-col gap-4">
        <Cartao titulo={t('broadcast.secoes.template')}>
          <div className="flex flex-col gap-3">
            <Campo rotulo={t('broadcast.template.escolher')}>
              {(id) => (
                <Selecao
                  id={id}
                  disabled={!rascunho}
                  value={r.template ? `${r.template.nome}|${r.template.idioma}` : ''}
                  onChange={(e) => escolherTemplate(e.target.value)}
                  vazio={t('broadcast.template.nenhum')}
                  opcoes={opcoesTemplate}
                />
              )}
            </Campo>
            {templates.isSuccess && !templates.data.itens.length && <p className="text-sm text-tinta-3">{t('broadcast.template.semAprovados')}</p>}
            {b.template && !b.template.resumo && !escolhido && <p className="text-sm text-laranja">{t('broadcast.template.mudou')}</p>}
            {resumo && (
              <>
                <p className="text-xs text-tinta-3">{t('broadcast.template.categoria', { categoria: t(`broadcast.template.categorias.${categoria}`, { defaultValue: categoria ?? '' }) })}</p>
                <p className="text-sm text-tinta-2">{tipo === 'marketing' ? t('broadcast.template.exigeMarketing') : t('broadcast.template.exigeAvisos')}</p>
                <div className="rounded-lg border border-borda bg-sup-2 px-3 py-2 text-sm whitespace-pre-wrap text-tinta-1" aria-label={t('broadcast.template.corpo')}>
                  {textoLivre(resumo.corpo)}
                </div>
              </>
            )}
          </div>
        </Cartao>
        {resumo && (
          <Cartao titulo={t('broadcast.secoes.variaveis')}>
            {r.variaveis.length ? (
              <div className="flex flex-col gap-3">
                {r.variaveis.map((o, i) =>
                  origemPara(o, (n) => setR((x) => ({ ...x, variaveis: x.variaveis.map((v, j) => (j === i ? n : v)) })), t('broadcast.variavel.rotulo', { n: resumo.nomes[i] ?? i + 1 })),
                )}
              </div>
            ) : (
              <p className="text-sm text-tinta-3">{t('broadcast.variavel.semVariaveis')}</p>
            )}
          </Cartao>
        )}
        {resumo?.cabecalho && r.extras.cabecalho && (
          <Cartao titulo={t('broadcast.secoes.cabecalho')}>
            {'link' in r.extras.cabecalho ? (
              <Campo rotulo={t('broadcast.cabecalho.link')} ajuda={t('broadcast.cabecalho.linkAjuda')}>
                {(id) => <Entrada id={id} type="url" disabled={!rascunho} maxLength={500} value={r.extras.cabecalho && 'link' in r.extras.cabecalho ? r.extras.cabecalho.link : ''} onChange={(e) => setR((x) => ({ ...x, extras: { ...x.extras, cabecalho: { link: e.target.value } } }))} />}
              </Campo>
            ) : (
              r.extras.cabecalho.variaveis.map((o, i) =>
                origemPara(o, (n) => setR((x) => ({ ...x, extras: { ...x.extras, cabecalho: { variaveis: [n] } } })), `${t('broadcast.cabecalho.variavel')} ${resumo.cabecalho?.tipo === 'texto' ? (resumo.cabecalho.nomes[i] ?? i + 1) : i + 1}`),
              )
            )}
          </Cartao>
        )}
        {resumo && (
          <Cartao titulo={t('broadcast.secoes.botoes')}>
            {resumo.botoes.length ? (
              <div className="flex flex-col gap-4">
                {resumo.botoes.map((bt) => (
                  <BotaoDoTemplate key={bt.indice} bt={bt} b={b} r={r} setR={setR} editavel={rascunho} colunas={r.publico.fonte === 'planilha' ? colunas : []} />
                ))}
              </div>
            ) : (
              <p className="text-sm text-tinta-3">{t('broadcast.botao.semBotoes')}</p>
            )}
          </Cartao>
        )}
      </div>
      <div className="flex min-w-0 flex-col gap-4">
        <PublicoCartao b={b} r={r} setR={setR} editavel={rascunho} planilhaNova={planilhaNova} setPlanilhaNova={setPlanilhaNova} sujo={sujo} />
        <Cartao titulo={t('broadcast.secoes.envio')}>
          <div className="grid gap-3 sm:grid-cols-2">
            <Campo rotulo={t('broadcast.envio.ritmo')} ajuda={t('broadcast.envio.ritmoAjuda')}>
              {(id) => (
                <Entrada
                  id={id}
                  type="number"
                  min={LIMITES_BROADCAST.ritmoMinimo}
                  max={LIMITES_BROADCAST.ritmoMaximo}
                  disabled={!ritmoEditavel}
                  value={r.ritmo}
                  onChange={(e) => setR((x) => ({ ...x, ritmo: Math.min(LIMITES_BROADCAST.ritmoMaximo, Math.max(LIMITES_BROADCAST.ritmoMinimo, Math.trunc(Number(e.target.value) || 1))) }))}
                />
              )}
            </Campo>
            <Campo rotulo={t('broadcast.envio.limite')} ajuda={t('broadcast.envio.limiteAjuda')}>
              {(id) => (
                <Selecao
                  id={id}
                  disabled={!ritmoEditavel}
                  value={r.limite_24h === null ? 'ilimitado' : String(r.limite_24h)}
                  onChange={(e) => setR((x) => ({ ...x, limite_24h: e.target.value === 'ilimitado' ? null : Number(e.target.value) }))}
                  opcoes={LIMITES_24H.map((l) => ({ valor: l === null ? 'ilimitado' : String(l), rotulo: t(`broadcast.envio.limites.${l === null ? 'ilimitado' : String(l)}`) }))}
                />
              )}
            </Campo>
          </div>
        </Cartao>
        {b.estado === 'rascunho' && (
          <Cartao titulo={t('broadcast.secoes.pendencias')}>
            {problemas.length ? (
              <ul className="flex list-disc flex-col gap-1 pl-5 text-sm text-laranja" data-pendencias-broadcast>
                {problemas.map((p) => (
                  <li key={p}>{t(`broadcast.problemas.${p.replace('broadcast.', '')}`)}</li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-verde">{t('broadcast.acoes.feito')}</p>
            )}
          </Cartao>
        )}
        {b.estado === 'rascunho' && <PreviaCartao b={b} sujo={sujo} />}
      </div>
    </div>
  );
}

/** De onde vem o valor: campo do cadastro, coluna da planilha ou texto fixo. */
function OrigemCampo({ rotulo, o, colunas, aoMudar, editavel }: { rotulo: string; o: OrigemVariavel; colunas: string[]; aoMudar: (o: OrigemVariavel) => void; editavel: boolean }) {
  const { t } = useTranslation('automacoes');
  const valorOrigem = o.origem === 'campo' ? `campo:${o.campo}` : o.origem === 'coluna' ? `coluna:${o.coluna}` : 'fixo';
  const opcoes = [
    ...CAMPOS_VARIAVEL.map((c) => ({ valor: `campo:${c}`, rotulo: `${t('broadcast.variavel.origens.campo')}: ${t(`broadcast.variavel.campos.${c}`)}` })),
    ...colunas.map((c) => ({ valor: `coluna:${c}`, rotulo: `${t('broadcast.variavel.origens.coluna')}: ${c}` })),
    { valor: 'fixo', rotulo: t('broadcast.variavel.origens.fixo') },
  ];
  if (o.origem === 'coluna' && !colunas.includes(o.coluna)) opcoes.push({ valor: valorOrigem, rotulo: `${t('broadcast.variavel.origens.coluna')}: ${o.coluna}` });
  return (
    <div className="flex min-w-0 flex-col gap-2">
      <Campo rotulo={rotulo}>
        {(id) => (
          <Selecao
            id={id}
            disabled={!editavel}
            value={valorOrigem}
            onChange={(e) => {
              const v = e.target.value;
              if (v === 'fixo') aoMudar({ origem: 'fixo', valor: '' });
              else if (v.startsWith('coluna:')) aoMudar({ origem: 'coluna', coluna: v.slice(7) });
              else aoMudar({ origem: 'campo', campo: v.slice(6) as (typeof CAMPOS_VARIAVEL)[number] });
            }}
            opcoes={opcoes}
          />
        )}
      </Campo>
      {o.origem === 'fixo' && (
        <Campo rotulo={`${rotulo} · ${t('broadcast.variavel.valor')}`}>
          {(id) => <Entrada id={id} disabled={!editavel} maxLength={LIMITES_BROADCAST.valorVariavel} value={o.valor} onChange={(e) => aoMudar({ origem: 'fixo', valor: e.target.value })} />}
        </Campo>
      )}
    </div>
  );
}

function BotaoDoTemplate({ bt, b, r, setR, editavel, colunas }: { bt: ResumoTemplate['botoes'][number]; b: Broadcast; r: Rascunho; setR: (f: (r: Rascunho) => Rascunho) => void; editavel: boolean; colunas: string[] }) {
  const { t } = useTranslation('automacoes');
  const rotulo = t('broadcast.botao.rotulo', { n: bt.indice + 1, texto: bt.rotulo });
  if (bt.tipo === 'resposta') {
    const cfg = r.botoes.find((x) => x.indice === bt.indice) ?? { indice: bt.indice, acao: 'nada' as const, fluxo_id: null };
    const mudar = (n: BotaoBroadcast) => setR((x) => ({ ...x, botoes: [...x.botoes.filter((y) => y.indice !== bt.indice), n].sort((a, z) => a.indice - z.indice) }));
    return (
      <fieldset className="flex min-w-0 flex-col gap-2" data-botao-broadcast={bt.indice}>
        <legend className="mb-1 text-sm font-medium text-tinta-1">{rotulo}</legend>
        <div className="flex min-w-0 flex-col gap-2">
          <Campo rotulo={t('broadcast.botao.acao')}>
            {(id) => (
              <Selecao
                id={id}
                disabled={!editavel}
                value={cfg.acao}
                onChange={(e) => mudar({ indice: bt.indice, acao: e.target.value as BotaoBroadcast['acao'], fluxo_id: null })}
                opcoes={(['nada', 'parar', 'fluxo'] as const).map((a) => ({ valor: a, rotulo: t(`broadcast.botao.acoes.${a}`) }))}
              />
            )}
          </Campo>
          {cfg.acao === 'fluxo' && (
            <Campo rotulo={t('broadcast.botao.fluxo')}>
              {(id) => (
                <Selecao
                  id={id}
                  disabled={!editavel}
                  value={cfg.fluxo_id ?? ''}
                  vazio={t('broadcast.botao.fluxo')}
                  onChange={(e) => mudar({ ...cfg, fluxo_id: e.target.value || null })}
                  opcoes={b.fluxos.map((f) => ({
                    valor: f.id,
                    rotulo: !f.publicado ? t('broadcast.botao.naoPublicada', { nome: f.nome }) : f.estado === 'pausado' ? t('broadcast.botao.pausada', { nome: f.nome }) : f.nome,
                  }))}
                />
              )}
            </Campo>
          )}
        </div>
        {cfg.acao === 'fluxo' && !b.fluxos.length && <p className="text-xs text-tinta-3">{t('broadcast.botao.semFluxos')}</p>}
      </fieldset>
    );
  }
  if (bt.tipo === 'link_dinamico') {
    const atual = r.extras.botoes.find((x) => x.indice === bt.indice && x.tipo === 'link');
    const o: OrigemVariavel = atual && atual.tipo === 'link' ? atual.valor : { origem: 'fixo', valor: '' };
    return (
      <OrigemCampo
        rotulo={`${rotulo} · ${t('broadcast.botao.link')}`}
        o={o}
        colunas={colunas}
        editavel={editavel}
        aoMudar={(n) => setR((x) => ({ ...x, extras: { ...x.extras, botoes: [...x.extras.botoes.filter((y) => y.indice !== bt.indice), { indice: bt.indice, tipo: 'link', valor: n }] } }))}
      />
    );
  }
  if (bt.tipo === 'codigo') {
    const atual = r.extras.botoes.find((x) => x.indice === bt.indice && x.tipo === 'codigo');
    return (
      <Campo rotulo={`${rotulo} · ${t('broadcast.botao.codigo')}`}>
        {(id) => (
          <Entrada
            id={id}
            disabled={!editavel}
            maxLength={15}
            value={atual && atual.tipo === 'codigo' ? atual.codigo : ''}
            onChange={(e) => setR((x) => ({ ...x, extras: { ...x.extras, botoes: [...x.extras.botoes.filter((y) => y.indice !== bt.indice), { indice: bt.indice, tipo: 'codigo', codigo: e.target.value.trim() }] } }))}
          />
        )}
      </Campo>
    );
  }
  return (
    <p className="text-sm text-tinta-3">
      {rotulo} · {t('broadcast.botao.outro')}
    </p>
  );
}

// ---------------------------------------------------------------------------
// Público
// ---------------------------------------------------------------------------

function alternar(lista: string[], v: string, marcado: boolean) {
  return marcado ? [...new Set([...lista, v])] : lista.filter((x) => x !== v);
}

function ListaMarcavel({ legenda, itens, marcados, aoMudar, editavel }: { legenda: string; itens: { valor: string; rotulo: string }[]; marcados: string[]; aoMudar: (l: string[]) => void; editavel: boolean }) {
  return (
    <fieldset className="flex min-w-0 flex-col gap-1.5">
      <legend className="mb-1.5 text-sm font-medium text-tinta-2">{legenda}</legend>
      <div className="grid max-h-64 gap-2 overflow-y-auto sm:grid-cols-2" tabIndex={-1}>
        {itens.map((i) => (
          <label key={i.valor} className="flex min-w-0 cursor-pointer items-center gap-2 rounded-lg border border-borda bg-sup-2 px-3 py-2 text-sm">
            <input type="checkbox" disabled={!editavel} checked={marcados.includes(i.valor)} onChange={(x) => aoMudar(alternar(marcados, i.valor, x.target.checked))} />
            <TextoCortado className="min-w-0">{i.rotulo}</TextoCortado>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

function PublicoCartao({
  b,
  r,
  setR,
  editavel,
  planilhaNova,
  setPlanilhaNova,
  sujo,
}: {
  b: Broadcast;
  r: Rascunho;
  setR: (f: (r: Rascunho) => Rascunho) => void;
  editavel: boolean;
  planilhaNova: { linhas: LinhaPlanilha[]; colunas: string[] } | null | undefined;
  setPlanilhaNova: (p: { linhas: LinhaPlanilha[]; colunas: string[] } | null | undefined) => void;
  sujo: boolean;
}) {
  const { t } = useTranslation('automacoes');
  const qc = useQueryClient();
  const toast = useToast();
  const arquivo = useRef<HTMLInputElement>(null);
  const p = r.publico;
  const mudar = (n: Partial<Publico>) => setR((x) => ({ ...x, publico: { ...x.publico, ...n } }));
  const etiquetas = useQuery({ queryKey: ['conversas', 'etiquetas', 'broadcast'], queryFn: () => get<{ itens: { nome: string; conversas: number }[] }>('/conversas/etiquetas'), enabled: p.fonte === 'etiquetas' && b.conversas });
  const tags = useQuery({ queryKey: ['cadastros', 'tags'], queryFn: () => getTodos<{ id: string; nome: string }>('/cadastros/tags'), enabled: p.fonte === 'segmento' });
  const setores = useQuery({ queryKey: ['cadastros', 'setores'], queryFn: () => getTodos<{ id: string; nome: string }>('/cadastros/setores'), enabled: p.fonte === 'segmento' });
  const parceiros = useQuery({
    queryKey: ['cadastros', 'parceiros', 'broadcast', p.parceiros],
    queryFn: async () => Promise.all(p.parceiros.slice(0, 50).map((id) => get<{ id: string; nome: string }>(`/cadastros/parceiros/${id}`).catch(() => ({ id, nome: id })))),
    enabled: p.fonte === 'cadastros' && p.parceiros.length > 0,
  });
  const declarar = useMutation({
    mutationFn: (v: boolean) => patch(`/automacoes/broadcasts/${b.id}`, { declarar: v }),
    onSuccess: () => qc.invalidateQueries({ queryKey: chaveBroadcasts }),
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const lido = planilhaNova !== undefined ? planilhaNova : null;

  const lerArquivo = async (f: File) => {
    const r2 = lerPlanilha(await f.text());
    if (!r2.ok) return toast.erro(t(`broadcast.publico.erros.${r2.erro.replace('planilha.', '')}`));
    setPlanilhaNova({ linhas: r2.linhas, colunas: r2.colunas });
  };

  return (
    <Cartao titulo={t('broadcast.secoes.publico')}>
      <div className="flex min-w-0 flex-col gap-3">
        <Campo rotulo={t('broadcast.publico.fonte')}>
          {(id) => (
            <Selecao
              id={id}
              disabled={!editavel}
              value={p.fonte}
              onChange={(e) => mudar({ fonte: e.target.value as FontePublico })}
              opcoes={(['etiquetas', 'cadastros', 'segmento', 'planilha'] as const).map((f) => ({ valor: f, rotulo: t(`broadcast.publico.fontes.${f}`) }))}
            />
          )}
        </Campo>
        {p.fonte === 'etiquetas' &&
          (etiquetas.data?.itens.length ? (
            <ListaMarcavel
              legenda={t('broadcast.publico.etiquetas')}
              itens={etiquetas.data.itens.map((e) => ({ valor: e.nome, rotulo: `${e.nome} (${numero(e.conversas)})` }))}
              marcados={p.etiquetas}
              aoMudar={(l) => mudar({ etiquetas: l })}
              editavel={editavel}
            />
          ) : (
            <p className="text-sm text-tinta-3">{t('broadcast.publico.semEtiquetas')}</p>
          ))}
        {p.fonte === 'cadastros' && (
          <div className="flex flex-col gap-2">
            {editavel && (
              <Campo rotulo={t('broadcast.publico.cadastro')}>
                {(id) => (
                  <BuscaRemota
                    id={id}
                    def={{ caminho: '/cadastros/parceiros', rotulo: (i) => String(i.nome), pessoa: true }}
                    valor=""
                    aoMudar={(v) => typeof v === 'string' && v && mudar({ parceiros: [...new Set([...p.parceiros, v])].slice(0, LIMITES_BROADCAST.destinatarios) })}
                  />
                )}
              </Campo>
            )}
            <ul className="flex flex-wrap gap-2" data-cadastros-broadcast>
              {p.parceiros.slice(0, 50).map((id) => {
                const nome = nomePessoa(parceiros.data?.find((x) => x.id === id)?.nome ?? '…');
                return (
                  <li key={id} className="inline-flex items-center gap-1 rounded-full border border-borda bg-sup-2 py-0.5 pr-1 pl-3 text-sm">
                    <TextoCortado className="max-w-48">{nome}</TextoCortado>
                    {editavel && (
                      <button type="button" className="rounded-full p-1 text-tinta-3 hover:text-tinta-1" aria-label={t('broadcast.publico.remover', { nome })} title={t('broadcast.publico.remover', { nome })} onClick={() => mudar({ parceiros: p.parceiros.filter((x) => x !== id) })}>
                        <X className="size-3.5" aria-hidden />
                      </button>
                    )}
                  </li>
                );
              })}
              {p.parceiros.length > 50 && <li className="text-sm text-tinta-3">+{numero(p.parceiros.length - 50)}</li>}
            </ul>
          </div>
        )}
        {p.fonte === 'segmento' && (
          <>
            <ListaMarcavel legenda={t('broadcast.publico.tags')} itens={(tags.data?.itens ?? []).map((x) => ({ valor: x.id, rotulo: x.nome }))} marcados={p.tags} aoMudar={(l) => mudar({ tags: l })} editavel={editavel} />
            <ListaMarcavel legenda={t('broadcast.publico.setores')} itens={(setores.data?.itens ?? []).map((x) => ({ valor: x.id, rotulo: x.nome }))} marcados={p.setores} aoMudar={(l) => mudar({ setores: l })} editavel={editavel} />
          </>
        )}
        {p.fonte === 'planilha' && (
          <div className="flex flex-col gap-3">
            <p className="text-xs text-tinta-3">{t('broadcast.publico.planilhaAjuda')}</p>
            <input ref={arquivo} type="file" accept=".csv,text/csv" className="sr-only" tabIndex={-1} aria-label={t('broadcast.publico.planilha')} title={t('broadcast.publico.planilha')} onChange={(e) => e.target.files?.[0] && void lerArquivo(e.target.files[0])} />
            {editavel && (
              <div className="flex flex-wrap gap-2">
                <Botao tamanho="sm" icone={<FileUp className="size-4" />} onClick={() => arquivo.current?.click()}>
                  {t('broadcast.publico.planilha')}
                </Botao>
                {(b.planilha_linhas > 0 || lido) && (
                  <Botao tamanho="sm" variante="fantasma" icone={<Trash2 className="size-4" />} onClick={() => setPlanilhaNova(null)}>
                    {t('broadcast.publico.apagarPlanilha')}
                  </Botao>
                )}
              </div>
            )}
            {lido ? (
              <p className="text-sm text-tinta-2">{t('broadcast.publico.planilhaLida', { n: numero(lido.linhas.length), colunas: ['telefone', 'nome', ...lido.colunas].join(', ') })}</p>
            ) : planilhaNova === null ? null : b.planilha_linhas ? (
              <p className="text-sm text-tinta-2">{t('broadcast.publico.planilhaSalva', { count: b.planilha_linhas })}</p>
            ) : null}
            {b.planilha_linhas > 0 && planilhaNova === undefined && (
              <label className="flex min-w-0 cursor-pointer items-start gap-2 rounded-lg border border-borda bg-sup-2 px-3 py-2 text-sm">
                <input type="checkbox" className="mt-0.5" disabled={!editavel || declarar.isPending || sujo} checked={!!b.declaracao} onChange={(e) => declarar.mutate(e.target.checked)} />
                <span className="min-w-0">
                  {t('broadcast.publico.declarar')}
                  {b.declaracao && b.declarado_em && (
                    <span className="mt-1 block text-xs text-tinta-3">{t('broadcast.publico.declarado', { nome: nomePessoa(b.declarado_por_nome ?? ''), quando: dataHora(b.declarado_em) })}</span>
                  )}
                </span>
              </label>
            )}
            <p className="text-xs text-tinta-3">{t('broadcast.publico.declaracaoAjuda')}</p>
          </div>
        )}
      </div>
    </Cartao>
  );
}

function PreviaCartao({ b, sujo }: { b: Broadcast; sujo: boolean }) {
  const { t } = useTranslation('automacoes');
  const toast = useToast();
  const previa = useMutation({ mutationFn: () => post<Previa>(`/automacoes/broadcasts/${b.id}/previa`, {}), onError: (e) => toast.erro(mensagemDeErro(e)) });
  const r = previa.data?.resumo;
  return (
    <Cartao
      titulo={t('broadcast.secoes.previa')}
      acao={
        <Botao tamanho="sm" icone={<UsersRound className="size-4" />} carregando={previa.isPending} onClick={() => (sujo ? toast.erro(t('broadcast.acoes.salveAntes')) : previa.mutate())}>
          {t('broadcast.acoes.conferir')}
        </Botao>
      }
    >
      {r ? (
        <div className="flex min-w-0 flex-col gap-3" data-previa-broadcast>
          <div className="grid gap-3 sm:grid-cols-2">
            <Indicador rotulo={t('broadcast.previa.recebem')} valor={numero(r.recebem)} detalhe={t('broadcast.relatorio.detalhe', { total: numero(r.encontrados) })} cor="verde" />
            <Indicador rotulo={t('broadcast.previa.semConsentimento')} valor={numero(r.sem_consentimento)} cor={r.sem_consentimento ? 'laranja' : 'tinta-1'} />
          </div>
          <ul className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-tinta-2">
            <li>{`${t('broadcast.previa.saiu')}: ${numero(r.saiu)}`}</li>
            <li>{`${t('broadcast.previa.invalidos')}: ${numero(r.telefone_invalido)}`}</li>
            <li>{`${t('broadcast.previa.repetidos')}: ${numero(r.repetido)}`}</li>
            <li>{`${t('broadcast.previa.variavelVazia')}: ${numero(r.variavel_vazia)}`}</li>
          </ul>
          {r.acima_do_teto && <p className="text-sm text-perigo">{t('broadcast.previa.acimaDoTeto')}</p>}
          {previa.data!.amostra.length > 0 && (
            <div className="flex flex-col gap-1">
              <h3 className="kicker">{t('broadcast.previa.amostra')}</h3>
              <ul className="flex flex-col divide-y divide-borda text-sm">
                {previa.data!.amostra.map((a) => (
                  <li key={a.telefone} className="flex min-w-0 items-center justify-between gap-2 py-1.5">
                    <span className="min-w-0">
                      <TextoCortado className="block">{a.nome ? `${nomePessoa(a.nome)} · ${telefoneTela(a.telefone)}` : telefoneTela(a.telefone)}</TextoCortado>
                      {a.recebe && a.variaveis.length > 0 && <TextoCortado className="block text-xs text-tinta-3">{a.variaveis.map((v) => textoLivre(v)).join(' · ')}</TextoCortado>}
                    </span>
                    <Selo cor={a.recebe ? 'verde' : 'neutro'}>{a.recebe ? t('broadcast.previa.recebe') : t(`broadcast.motivos.${a.motivo}`, { defaultValue: t('broadcast.previa.naoRecebe') })}</Selo>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ) : (
        <p className="text-sm text-tinta-3">{t('broadcast.acoes.conferir')}</p>
      )}
    </Cartao>
  );
}

// ---------------------------------------------------------------------------
// Relatório
// ---------------------------------------------------------------------------

type FiltroDestinatario = 'todos' | 'clicaram' | Destinatario['estado'];

function Relatorio({ b }: { b: Broadcast }) {
  const { t } = useTranslation('automacoes');
  const [filtro, setFiltro] = useState<FiltroDestinatario>('todos');
  const c = b.contagem;
  if (!c.total) return <Vazio texto={t('broadcast.relatorio.semRelatorio')} />;
  const pct = (n: number) => (c.enviados ? `${Math.round((n / c.enviados) * 100)}%` : '');
  const rotuloBotao = (i: number) => b.template?.resumo?.botoes.find((x) => x.indice === i)?.rotulo ?? t('broadcast.botao.rotulo', { n: i + 1, texto: '' });
  const textoErro = (codigo: number | null) => (codigo === null ? t('broadcast.erros.semCodigo') : t(`broadcast.erros.${codigo}`, { defaultValue: t('broadcast.erros.outro', { codigo }) }));
  const colunas: Coluna<Destinatario>[] = [
    { chave: 'telefone', rotulo: t('broadcast.destinatario.telefone'), principal: true, render: (d) => telefoneTela(d.telefone) },
    { chave: 'nome', rotulo: t('broadcast.destinatario.nome'), render: (d) => (d.nome ? nomePessoa(d.nome) : '') },
    { chave: 'estado', rotulo: t('broadcast.destinatario.estado'), render: (d) => <Selo cor={COR_DESTINATARIO[d.estado]}>{t(`broadcast.estadosDestinatario.${d.estado}`)}</Selo> },
    {
      chave: 'motivo',
      rotulo: t('broadcast.destinatario.detalhe'),
      render: (d) =>
        d.estado === 'falhou'
          ? d.erro_codigo !== null
            ? textoErro(d.erro_codigo)
            : textoLivre(d.motivo ?? t('broadcast.erros.semCodigo'))
          : d.estado === 'ignorado'
            ? t(`broadcast.motivos.${d.motivo}`, { defaultValue: d.motivo ?? '' })
            : d.erro_codigo !== null
              ? `${textoErro(d.erro_codigo)} · ${t('broadcast.destinatario.tentativas', { count: d.tentativas })}`
              : d.lido_em
                ? dataHora(d.lido_em)
                : d.entregue_em
                  ? dataHora(d.entregue_em)
                  : d.despachado_em
                    ? dataHora(d.despachado_em)
                    : '',
    },
    { chave: 'botao', rotulo: t('broadcast.destinatario.botao'), render: (d) => (d.botao !== null ? rotuloBotao(d.botao) : '') },
    { chave: 'saiu_em', rotulo: t('broadcast.destinatario.saiu'), render: (d) => (d.saiu_em ? dataHora(d.saiu_em) : '') },
  ];
  const filtros: FiltroDestinatario[] = ['todos', 'pendente', 'enviado', 'entregue', 'lido', 'falhou', 'ignorado', 'clicaram'];
  return (
    <div className="flex min-w-0 flex-col gap-4" data-relatorio-broadcast>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Indicador rotulo={t('broadcast.relatorio.enviados')} valor={numero(c.enviados)} detalhe={t('broadcast.relatorio.detalhe', { total: numero(c.total) })} />
        <Indicador rotulo={t('broadcast.relatorio.entregues')} valor={numero(c.entregues)} detalhe={t('broadcast.relatorio.taxa', { pct: pct(c.entregues) })} cor="ciano" />
        <Indicador rotulo={t('broadcast.relatorio.lidos')} valor={numero(c.lidos)} detalhe={t('broadcast.relatorio.taxa', { pct: pct(c.lidos) })} cor="verde" />
        <Indicador rotulo={t('broadcast.relatorio.cliques')} valor={numero(c.cliques)} detalhe={t('broadcast.relatorio.taxa', { pct: pct(c.cliques) })} cor="dourado" />
        <Indicador rotulo={t('broadcast.relatorio.falhas')} valor={numero(c.falhas)} cor={c.falhas ? 'perigo' : 'tinta-1'} />
        <Indicador rotulo={t('broadcast.relatorio.ignorados')} valor={numero(c.ignorados)} />
        <Indicador rotulo={t('broadcast.relatorio.saidas')} valor={numero(c.saidas)} cor={c.saidas ? 'laranja' : 'tinta-1'} />
        <Indicador rotulo={t('broadcast.relatorio.total')} valor={numero(c.total)} />
      </div>
      <div className="grid gap-4 lg:grid-cols-3">
        <Cartao titulo={t('broadcast.relatorio.porBotao')}>
          {b.por_botao.length ? (
            <ul className="flex flex-col divide-y divide-borda text-sm">
              {b.por_botao.map((x) => (
                <li key={x.indice} className="flex justify-between gap-2 py-1.5">
                  <TextoCortado className="min-w-0">{rotuloBotao(x.indice)}</TextoCortado>
                  <span className="shrink-0 tabular-nums">{numero(x.cliques)}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-tinta-3">{t('broadcast.relatorio.nada')}</p>
          )}
        </Cartao>
        <Cartao titulo={t('broadcast.relatorio.porErro')}>
          {b.por_erro.length ? (
            <ul className="flex flex-col divide-y divide-borda text-sm">
              {b.por_erro.map((x) => (
                <li key={String(x.codigo)} className="flex justify-between gap-2 py-1.5">
                  <span className="min-w-0">{textoErro(x.codigo)}</span>
                  <span className="shrink-0 tabular-nums">{numero(x.total)}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-tinta-3">{t('broadcast.relatorio.nada')}</p>
          )}
        </Cartao>
        <Cartao titulo={t('broadcast.relatorio.porMotivo')}>
          {b.por_motivo.length ? (
            <ul className="flex flex-col divide-y divide-borda text-sm">
              {b.por_motivo.map((x) => (
                <li key={x.motivo} className="flex justify-between gap-2 py-1.5">
                  <span className="min-w-0">{t(`broadcast.motivos.${x.motivo}`, { defaultValue: x.motivo })}</span>
                  <span className="shrink-0 tabular-nums">{numero(x.total)}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-tinta-3">{t('broadcast.relatorio.nada')}</p>
          )}
        </Cartao>
      </div>
      <Cartao titulo={t('broadcast.relatorio.destinatarios')}>
        <Tabela<Destinatario>
          tela="automacoes.broadcast.destinatarios"
          caminho={`/automacoes/broadcasts/${b.id}/destinatarios`}
          params={filtro === 'todos' ? {} : filtro === 'clicaram' ? { clicou: true } : { estado: filtro }}
          chaveConsulta={[...chaveBroadcasts, 'destinatarios', b.id, filtro, b.updated_at]}
          colunas={colunas}
          filtros={{ filtro }}
          aoAplicarFiltros={(x) => setFiltro(filtros.includes(x.filtro as FiltroDestinatario) ? (x.filtro as FiltroDestinatario) : 'todos')}
          filtrosExtra={
            <Segmentado<FiltroDestinatario>
              rotulo={t('broadcast.destinatario.filtroEstado')}
              valor={filtro}
              opcoes={filtros.map((f) => ({ valor: f, rotulo: f === 'todos' ? t('broadcast.destinatario.todos') : f === 'clicaram' ? t('broadcast.destinatario.clicaram') : t(`broadcast.estadosDestinatario.${f}`) }))}
              aoMudar={setFiltro}
            />
          }
          vazio={<Vazio texto={t('broadcast.relatorio.semDestinatarios')} />}
        />
      </Cartao>
    </div>
  );
}
